import React from 'react';
import { Layers, CheckCircle2, Zap, Clock, Navigation2, ShieldCheck } from 'lucide-react';

export default function KpiGrid({ summary }) {
  if (!summary) return null;

  const total = summary.totalSessionsInitiated || 0;
  const completed = summary.totalSessionsCompleted || 0;
  const abandoned = summary.totalSessionsAbandoned || 0;
  const completionRate = summary.completionRate || 0;
  const currentConcurrent = summary.currentConcurrentSessions || 0;
  const peakConcurrent = summary.peakConcurrentSessions || 0;
  const avgDuration = summary.avgSessionDurationSeconds || 0;
  const avgHops = summary.avgMenuHopsPerSession || 0;
  const uptime = summary.uptimePercentage || 99.98;
  const slaStatus = summary.slaComplianceStatus || 'COMPLIANT';

  return (
    <div className="kpi-grid">
      
      {/* 1. Total Sessions */}
      <div className="glass-card" style={{ padding: '20px', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Total USSD Sessions
            </span>
            <div style={{ fontSize: '28px', fontWeight: '800', marginTop: '6px', color: 'var(--text-primary)' }}>
              {total.toLocaleString()}
            </div>
          </div>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'rgba(248, 65, 34, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Layers size={20} color="var(--accent-orange)" />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '14px', fontSize: '12px' }}>
          <span style={{ color: '#059669', fontWeight: '600' }}>
            ✓ {completed.toLocaleString()} Completed
          </span>
          <span style={{ color: '#DC2626', fontWeight: '600' }}>
            ✕ {abandoned.toLocaleString()} Dropped
          </span>
        </div>
      </div>

      {/* 2. Completion Rate */}
      <div className="glass-card" style={{ padding: '20px', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Completion Rate
            </span>
            <div style={{ fontSize: '28px', fontWeight: '800', marginTop: '6px', color: '#059669' }}>
              {completionRate}%
            </div>
          </div>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'rgba(16, 185, 129, 0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <CheckCircle2 size={20} color="#059669" />
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{ marginTop: '16px', background: '#E2E8F0', height: '6px', borderRadius: '999px', overflow: 'hidden' }}>
          <div style={{
            width: `${Math.min(100, completionRate)}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #10B981, #059669)',
            borderRadius: '999px',
            transition: 'width 0.8s ease'
          }} />
        </div>
      </div>

      {/* 3. Uptime & SLA Compliance */}
      <div className="glass-card" style={{ padding: '20px', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Uptime & SLA
            </span>
            <div style={{ fontSize: '28px', fontWeight: '800', marginTop: '6px', color: slaStatus === 'COMPLIANT' ? '#059669' : '#D97706' }}>
              {uptime}%
            </div>
          </div>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'rgba(16, 185, 129, 0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <ShieldCheck size={20} color="#059669" />
          </div>
        </div>

        <div style={{ marginTop: '14px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            padding: '3px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '700',
            background: slaStatus === 'COMPLIANT' ? 'rgba(16, 185, 129, 0.12)' : 'rgba(245, 158, 11, 0.12)',
            color: slaStatus === 'COMPLIANT' ? '#059669' : '#D97706'
          }}>
            {slaStatus}
          </span>
          <span style={{ color: 'var(--text-muted)' }}>Target: 99.90%</span>
        </div>
      </div>

      {/* 4. Live Concurrency */}
      <div className="glass-card" style={{ padding: '20px', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Active Sessions
            </span>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '6px' }}>
              <div style={{ fontSize: '28px', fontWeight: '800', color: 'var(--accent-orange)' }}>
                {currentConcurrent}
              </div>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>current</span>
            </div>
          </div>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'rgba(248, 65, 34, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Zap size={20} color="var(--accent-orange)" />
          </div>
        </div>

        <div style={{ marginTop: '14px', fontSize: '12px', color: 'var(--text-secondary)' }}>
          Peak Concurrency: <strong style={{ color: 'var(--text-primary)' }}>{peakConcurrent}</strong> sessions
        </div>
      </div>

      {/* 5. Average Duration */}
      <div className="glass-card" style={{ padding: '20px', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Avg Duration
            </span>
            <div style={{ fontSize: '28px', fontWeight: '800', marginTop: '6px', color: 'var(--text-primary)' }}>
              {avgDuration} <span style={{ fontSize: '14px', color: 'var(--text-muted)', fontWeight: '500' }}>sec</span>
            </div>
          </div>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'rgba(139, 92, 246, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Clock size={20} color="var(--accent-purple)" />
          </div>
        </div>

        <div style={{ marginTop: '14px', fontSize: '12px', color: 'var(--text-muted)' }}>
          Latency per round-trip: ~0.8s
        </div>
      </div>

      {/* 6. Average Depth / Hops */}
      <div className="glass-card" style={{ padding: '20px', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Avg Menu Depth
            </span>
            <div style={{ fontSize: '28px', fontWeight: '800', marginTop: '6px', color: 'var(--text-primary)' }}>
              {avgHops} <span style={{ fontSize: '14px', color: 'var(--text-muted)', fontWeight: '500' }}>hops</span>
            </div>
          </div>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'rgba(6, 182, 212, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Navigation2 size={20} color="var(--accent-cyan)" />
          </div>
        </div>

        <div style={{ marginTop: '14px', fontSize: '12px', color: 'var(--text-muted)' }}>
          Typical path: Dial → Menu → PIN → Output
        </div>
      </div>

    </div>
  );
}
